export const UTILS = `import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs.filter(Boolean)));
}
`
export const TAILWIND_CONFIG_WITH_VARIABLES = `import type { Config } from "tailwindcss";
export default {
	darkMode: ["class"],
	content: [
		'./pages/**/*.{js,ts,jsx,tsx,mdx}',
    	'./components/**/*.{js,ts,jsx,tsx,mdx}',
    	'./app/**/*.{js,ts,jsx,tsx,mdx}',
    	'./src/**/*.{js,ts,jsx,tsx,mdx}',
	],
	safelist: ["font-heading", "font-body", "heading-1", "heading-2", "heading-3", "heading-4", "heading-5", "heading-6", "body-lg", "body-base", "body-sm", "body-xs"],
	theme: {
		extend: {
			colors: {
				bg1: "hsl(var(--bg1))",
				bg2: "hsl(var(--bg2))",
				bg3: "hsl(var(--bg3))",
				bg4: "hsl(var(--bg4))",
				fg1: "hsl(var(--fg1))",
				fg2: "hsl(var(--fg2))",
				fg3: "hsl(var(--fg3))",
				information: "hsl(var(--information))",
				success: "hsl(var(--success))",
				error: "hsl(var(--error))",
				warning: "hsl(var(--warning))",
				white: "hsl(var(--static-white))",
				black: "hsl(var(--static-black))",
				primary: "hsl(var(--primary))",
				border: {
					DEFAULT: "hsl(var(--border))",
					secondary: "hsl(var(--border-secondary))",
					information: "hsl(var(--border-information))",
					success: "hsl(var(--border-success))",
					error: "hsl(var(--border-error))",
					warning: "hsl(var(--border-warning))",
				},
			},
			borderRadius: {
				lg: "var(--radius)",
				md: "calc(var(--radius) - 2px)",
				sm: "calc(var(--radius) - 4px)",
			},
			keyframes: {
				"accordion-down": {
					from: { height: "0" },
					to: { height: "var(--radix-accordion-content-height)" },
				},
				"accordion-up": {
					from: { height: "var(--radix-accordion-content-height)" },
					to: { height: "0" },
				},
			},
			animation: {
				"accordion-down": "accordion-down 0.2s ease-out",
				"accordion-up": "accordion-up 0.2s ease-out",
			},
		},
	},
} satisfies Config;`

export const GLOBAL_CSS = `@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");

@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
	:root {
		--primary: 258 70% 50%;
		--bg1: 0 0% 100%;
		--bg2: 0 0% 98%;
		--bg3: 0 0% 95%;
		--bg4: 0 0% 92%;
		--fg1: 240 10% 10%;
		--fg2: 240 4% 35%;
		--fg3: 240 4% 60%;
		--border: 240 2% 90%;
		--border-secondary: 240 4% 80%;
		--radius: 0.5rem;
		--static-white: 0 0% 100%;
		--static-black: 240 10% 10%;
		--information: 210 100% 50%;
		--success: 135 50% 44%;
		--error: 0 100% 60%;
		--warning: 35 100% 50%;

		/* shadcn */
		--card: 0 0% 100%;
		--card-foreground: 0 0% 3.9%;
		--popover: 0 0% 100%;
		--popover-foreground: 0 0% 3.9%;
		--muted: 0 0% 96.1%;
		--muted-foreground: 0 0% 45.1%;
		--accent: 0 0% 96.1%;
		--accent-foreground: 0 0% 9%;
		--destructive: 0 84.2% 60.2%;
		--destructive-foreground: 0 0% 98%;
		--input: 0 0% 89.8%;
		--ring: 0 0% 3.9%;
		--chart-1: 12 76% 61%;
		--chart-2: 173 58% 39%;
		--chart-3: 197 37% 24%;
		--chart-4: 43 74% 66%;
		--chart-5: 27 87% 67%;
	}

	.dark {
		--primary: 258 80% 52%;
		--bg1: 240 9% 2%;
		--bg2: 240 7% 8%;
		--bg3: 240 8% 12%;
		--bg4: 240 6% 16%;
		--fg1: 0 0% 100%;
		--fg2: 240 4% 60%;
		--fg3: 240 4% 40%;
		--border: 240 4% 15%;
		--border-secondary: 240 3% 30%;
		--information: 210 100% 50%;
		--success: 135 50% 44%;
		--error: 0 100% 60%;
		--warning: 35 100% 50%;
		--radius: 0.5rem;
		--static-white: 0 0% 100%;
		--static-black: 240 10% 10%;
		--information: 210 100% 52%;
		--success: 135 60% 46%;
		--error: 0 100% 62%;
		--warning: 35 100% 52%;
	}


}

@layer base {
	* {
		@apply border-border;
		scrollbar-width: thin;
		scrollbar-color: hsl(var(--border)) transparent;
		scroll-behavior: smooth;
	}

	html {
		font-family: "Inter", system-ui, sans-serif;
	}

	body {
		@apply bg-[hsl(var(--bg1))] text-[hsl(var(--fg1))];
	}

	code[data-line-numbers] {
		counter-reset: line;
	}

	code[data-line-numbers]>[data-line]::before {
		counter-increment: line;
		content: counter(line);

		/* Other styling */
		display: inline-block;
		width: 0.75rem;
		margin-right: 2rem;
		text-align: right;
		color: gray;
	}

	code[data-line-numbers-max-digits="2"]>[data-line]::before {
		width: 1.25rem;
	}

	code[data-line-numbers-max-digits="3"]>[data-line]::before {
		width: 1.75rem;
	}

	code[data-line-numbers-max-digits="4"]>[data-line]::before {
		width: 2.25rem;
	}
}

@layer utilities {

	/* Base typography classes for headings and body */
	.font-heading {
		font-family: "Inter Display", system-ui, sans-serif;
	}

	.font-body {
		font-family: "Inter", system-ui, sans-serif;
	}

	/* Heading styles */
	.heading-1 {
		@apply font-heading text-[2.5rem] font-bold leading-[2.75rem];

		@screen sm {
			font-size: 3rem;
			line-height: 3.5rem;
		}

		@screen lg {
			font-size: 4rem;
			line-height: 4.5rem;
		}
	}

	.heading-2 {
		@apply font-heading text-[2rem] font-bold leading-[2.5rem];

		@screen sm {
			font-size: 2.5rem;
			line-height: 3rem;
		}

		@screen lg {
			font-size: 3rem;
			line-height: 3.5rem;
		}
	}

	.heading-3 {
		@apply font-heading text-[1.5rem] font-bold leading-[2rem];

		@screen sm {
			font-size: 2rem;
			line-height: 2.5rem;
		}

		@screen lg {
			font-size: 2.5rem;
			line-height: 3rem;
		}
	}

	.heading-4 {
		@apply font-heading text-[1.25rem] font-bold leading-[1.75rem];

		@screen sm {
			font-size: 1.5rem;
			line-height: 2rem;
		}

		@screen lg {
			font-size: 2rem;
			line-height: 2.5rem;
		}
	}

	.heading-5 {
		@apply font-heading text-[1.125rem] font-bold leading-[1.75rem];

		@screen sm {
			font-size: 1.125rem;
			line-height: 1.75rem;
		}

		@screen lg {
			font-size: 1.5rem;
			line-height: 2rem;
		}
	}

	.heading-6 {
		@apply font-heading text-[1rem] font-bold leading-[1.5rem];

		@screen sm {
			font-size: 1.125rem;
			line-height: 1.75rem;
		}

		@screen lg {
			font-size: 1.25rem;
			line-height: 1.75rem;
		}
	}

	/* Body text styles */
	.body-lg {
		@apply font-body text-[1.125rem] leading-[1.75rem];
	}

	.body-base {
		@apply font-body text-[1rem] leading-[1.5rem];
	}

	.body-sm {
		@apply font-body text-[0.875rem] leading-[1.25rem];
	}

	.body-xs {
		@apply font-body text-[0.75rem] leading-[1rem];
	}

	/* Custom scrollbar styling. */
	::-webkit-scrollbar {
		width: 6px;
	}

	::-webkit-scrollbar-track {
		background: transparent;
	}

	::-webkit-scrollbar-thumb {
		background: hsl(var(--border));
		border-radius: 5px;
	}

	::-webkit-scrollbar-thumb:hover {
		background: hsl(var(--border-secondary));
	}

	.no-scrollbar {
		-ms-overflow-style: none;
		/* IE and Edge */
		scrollbar-width: none;
		/* Firefox */
	}

	.no-scrollbar::-webkit-scrollbar {
		display: none;
		/* Chrome, Safari and Opera */
	}

	/* Chrome & other browser puts a background color on autofill inputs,
	 this removes it */
	input:-webkit-autofill,
	input:-webkit-autofill:hover,
	input:-webkit-autofill:focus,
	input:-webkit-autofill:active {
		-webkit-background-clip: text;
		-webkit-text-fill-color: inherit;
		transition: background-color 5000s ease-in-out 0s;
		box-shadow: inset 0 0 20px 20px transparent;
	}

	/* Hide the number stepper for input[type="number"] */
	input[type="number"].hide-stepper::-webkit-outer-spin-button,
	input[type="number"].hide-stepper::-webkit-inner-spin-button,
	input[type="number"].hide-stepper {
		-webkit-appearance: none;
		margin: 0;
		-moz-appearance: textfield !important;
	}
}
`
export const GLOBAL_CSS_V4 = `@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap")
layer(base);

@import "tailwindcss";

@plugin "tailwindcss-animate";

@custom-variant dark (&:is(.dark *));

:root {
	/* primary */
	--color-primary-50: hsla(250, 100%, 98%, 1);
	--color-primary-100: hsla(250, 100%, 95%, 1);
	--color-primary-200: hsla(250, 100%, 92%, 1);
	--color-primary-300: hsla(250, 100%, 86%, 1);
	--color-primary-400: hsla(250, 100%, 76%, 1);
	--color-primary-500: hsla(250, 100%, 68%, 1);
	--color-primary-600: hsla(250, 80%, 60%, 1);
	--color-primary-700: hsla(250, 60%, 52%, 1);
	--color-primary-800: hsla(250, 55%, 44%, 1);
	--color-primary-900: hsla(250, 50%, 24%, 1);
	--color-primary-950: hsla(250, 50%, 15%, 1);

	/* information */
	--color-information-50: hsla(210, 100%, 98%, 1);
	--color-information-100: hsla(210, 100%, 95%, 1);
	--color-information-200: hsla(210, 100%, 92%, 1);
	--color-information-300: hsla(210, 100%, 86%, 1);
	--color-information-400: hsla(210, 100%, 76%, 1);
	--color-information-500: hsla(210, 100%, 68%, 1);
	--color-information-600: hsla(210, 100%, 60%, 1);
	--color-information-700: hsla(210, 100%, 50%, 1);
	--color-information-800: hsla(210, 80%, 42%, 1);
	--color-information-900: hsla(210, 60%, 24%, 1);
	--color-information-950: hsla(210, 50%, 16%, 1);

	/* success */
	--color-success-50: hsla(135, 100%, 98%, 1);
	--color-success-100: hsla(135, 80%, 95%, 1);
	--color-success-200: hsla(135, 80%, 95%, 1);
	--color-success-300: hsla(135, 80%, 95%, 1);
	--color-success-400: hsla(135, 80%, 95%, 1);
	--color-success-500: hsla(135, 80%, 95%, 1);
	--color-success-600: hsla(135, 70%, 56%, 1);
	--color-success-700: hsla(135, 70%, 56%, 1);
	--color-success-800: hsla(135, 50%, 44%, 1);
	--color-success-900: hsla(135, 50%, 24%, 1);
	--color-success-950: hsla(135, 50%, 16%, 1);

	/* error */
	--color-error-50: hsla(0, 100%, 98%, 1);
	--color-error-100: hsla(0, 100%, 95%, 1);
	--color-error-200: hsla(0, 100%, 92%, 1);
	--color-error-300: hsla(0, 100%, 86%, 1);
	--color-error-400: hsla(0, 100%, 86%, 1);
	--color-error-500: hsla(0, 90%, 68%, 1);
	--color-error-600: hsla(0, 90%, 68%, 1);
	--color-error-700: hsla(0, 70%, 52%, 1);
	--color-error-800: hsla(0, 65%, 44%, 1);
	--color-error-900: hsla(0, 60%, 24%, 1);
	--color-error-950: hsla(0, 50%, 16%, 1);

	/* warning */
	--color-warning-50: hsla(35, 100%, 98%, 1);
	--color-warning-100: hsla(35, 100%, 95%, 1);
	--color-warning-200: hsla(35, 100%, 92%, 1);
	--color-warning-300: hsla(35, 100%, 92%, 1);
	--color-warning-400: hsla(35, 100%, 76%, 1);
	--color-warning-500: hsla(35, 100%, 68%, 1);
	--color-warning-600: hsla(35, 100%, 60%, 1);
	--color-warning-700: hsla(35, 100%, 50%, 1);
	--color-warning-800: hsla(35, 80%, 44%, 1);
	--color-warning-900: hsla(35, 70%, 24%, 1);
	--color-warning-950: hsla(35, 50%, 16%, 1);

	/* neutral */
	--color-neutral-50: hsla(0, 0%, 98%, 1);
	--color-neutral-100: hsla(250, 5%, 96%, 1);
	--color-neutral-200: hsla(250, 6%, 92%, 1);
	--color-neutral-300: hsla(250, 5%, 86%, 1);
	--color-neutral-400: hsla(250, 5%, 68%, 1);
	--color-neutral-500: hsla(250, 5%, 46%, 1);
	--color-neutral-600: hsla(250, 5%, 34%, 1);
	--color-neutral-700: hsla(250, 5%, 26%, 1);
	--color-neutral-800: hsla(250, 5%, 16%, 1);
	--color-neutral-900: hsla(250, 6%, 10%, 1);
	--color-neutral-950: hsla(250, 4%, 4%, 1);

	/* static */
	--color-static-white: hsl(0 0% 100%);
	--color-static-black: hsla(250, 4%, 4%, 1);
}

@theme {
	/* primary */
	--color-primary: var(--color-primary-600);
	--color-primary-accent: var(--color-primary-50);
	--color-primary-accent-secondary: var(--color-primary-100);
	--color-primary-focus: var(--color-primary-200);
	--color-primary-stroke: var(--color-primary-400);
	--color-primary-hover: var(--color-primary-500);
	--color-primary-text: var(--color-primary-800);

	/* information */
	--color-information: var(--color-information-700);
	--color-information-accent: var(--color-information-50);
	--color-information-accent-secondary: var(--color-information-100);
	--color-information-focus: var(--color-information-200);
	--color-information-stroke: var(--color-information-400);
	--color-information-hover: var(--color-information-500);
	--color-information-text: var(--color-information-800);

	/* success */
	--color-success: var(--color-success-700);
	--color-success-accent: var(--color-success-50);
	--color-success-accent-secondary: var(--color-success-100);
	--color-success-focus: var(--color-success-200);
	--color-success-stroke: var(--color-success-400);
	--color-success-hover: var(--color-success-500);
	--color-success-text: var(--color-success-800);

	/* error */
	--color-error: var(--color-error-600);
	--color-error-accent: var(--color-error-50);
	--color-error-accent-secondary: var(--color-error-100);
	--color-error-focus: var(--color-error-200);
	--color-error-stroke: var(--color-error-400);
	--color-error-hover: var(--color-error-500);
	--color-error-text: var(--color-error-800);

	/* warning */
	--color-warning: var(--color-warning-700);
	--color-warning-accent: var(--color-warning-50);
	--color-warning-accent-secondary: var(--color-warning-100);
	--color-warning-focus: var(--color-warning-200);
	--color-warning-stroke: var(--color-warning-400);
	--color-warning-hover: var(--color-warning-500);
	--color-warning-text: var(--color-warning-800);

	/* background */
	--color-bg1: var(--color-static-white);
	--color-bg2: var(--color-neutral-50);
	--color-bg3: var(--color-neutral-100);
	--color-bg4: var(--color-neutral-200);

	/* foreground */
	--color-fg1: var(--color-neutral-950);
	--color-fg2: var(--color-neutral-700);
	--color-fg3: var(--color-neutral-400);

	/* text */
	--color-text-primary: var(--color-neutral-950);
	--color-text-paragraph: var(--color-neutral-700);
	--color-text-disabled: var(--color-neutral-500);

	/* stroke */
	--color-stroke: var(--color-neutral-300);
	--color-stroke-decorative: var(--color-neutral-200);

	/* Breakpoints */
	--breakpoint-laptop: 90rem;

	/* Animations */
	--animate-accordion-down: accordion-down 0.2s ease-out;
	--animate-accordion-up: accordion-up 0.2s ease-out;
	--animate-caret-blink: caret-blink 1.1s ease-out infinite;

	@keyframes accordion-down {
		from {
			height: 0;
		}
		to {
			height: var(--radix-accordion-content-height);
		}
	}

	@keyframes accordion-up {
		from {
			height: var(--radix-accordion-content-height);
		}
		to {
			height: 0;
		}
	}

	@keyframes caret-blink {
		0%,
		70%,
		100% {
			opacity: 1;
		}

		20%,
		50% {
			opacity: 0;
		}
	}
}

.dark {
	/* primary */
	--color-primary: var(--color-primary-600);
	--color-primary-accent: var(--color-primary-950);
	--color-primary-accent-secondary: var(--color-primary-900);
	--color-primary-focus: var(--color-primary-800);
	--color-primary-stroke: var(--color-primary-800);
	--color-primary-hover: var(--color-primary-400);
	--color-primary-text: var(--color-primary-400);

	/* information */
	--color-information: var(--color-information-700);
	--color-information-accent: var(--color-information-950);
	--color-information-accent-secondary: var(--color-information-900);
	--color-information-focus: var(--color-information-800);
	--color-information-stroke: var(--color-information-800);
	--color-information-hover: var(--color-information-400);
	--color-information-text: var(--color-information-400);

	/* success */
	--color-success: var(--color-success-700);
	--color-success-accent: var(--color-success-950);
	--color-success-accent-secondary: var(--color-success-900);
	--color-success-focus: var(--color-success-800);
	--color-success-stroke: var(--color-success-800);
	--color-success-hover: var(--color-success-400);
	--color-success-text: var(--color-success-400);

	/* error */
	--color-error: var(--color-error-600);
	--color-error-accent: var(--color-error-950);
	--color-error-accent-secondary: var(--color-error-900);
	--color-error-focus: var(--color-error-800);
	--color-error-stroke: var(--color-error-800);
	--color-error-hover: var(--color-error-400);
	--color-error-text: var(--color-error-400);

	/* warning */
	--color-warning: var(--color-warning-700);
	--color-warning-accent: var(--color-warning-950);
	--color-warning-accent-secondary: var(--color-warning-900);
	--color-warning-focus: var(--color-warning-800);
	--color-warning-stroke: var(--color-warning-800);
	--color-warning-hover: var(--color-warning-400);
	--color-warning-text: var(--color-warning-400);

	/* Background */
	--color-bg1: var(--color-static-black);
	--color-bg2: var(--color-neutral-900);
	--color-bg3: var(--color-neutral-800);
	--color-bg4: var(--color-neutral-700);

	/* foreground */
	--color-fg1: var(--color-neutral-50);
	--color-fg2: var(--color-neutral-400);
	--color-fg3: var(--color-neutral-700);

	/* stroke */
	--color-stroke: var(--color-neutral-800);
	--color-stroke-decorative: var(--color-neutral-900);
}

@layer base {
	*,
	::after,
	::before,
	::backdrop,
	::file-selector-button {
		border-color: var(--color-gray-200, currentColor);
	}
}

@utility font-heading {
	/* Base typography classes for headings and body */
	font-family: "Inter Display", system-ui, sans-serif;
}

@utility font-body {
	font-family: "Inter", system-ui, sans-serif;
}

@utility heading-1 {
	/* Heading styles */
	@apply font-heading text-[2.25rem] leading-[2.75rem] font-bold;

	@media (width >= theme(--breakpoint-sm)) {
		font-size: 3rem;
		line-height: 3.5rem;
	}

	@media (width >= theme(--breakpoint-lg)) {
		font-size: 4rem;
		line-height: 4.5rem;
	}
}

@utility heading-2 {
	@apply font-heading text-[2rem] leading-[2.5rem] font-bold;

	@media (width >= theme(--breakpoint-sm)) {
		font-size: 2.5rem;
		line-height: 3rem;
	}

	@media (width >= theme(--breakpoint-lg)) {
		font-size: 3rem;
		line-height: 3.5rem;
	}
}

@utility heading-3 {
	@apply font-heading text-[1.875rem] leading-[2.375rem] font-bold;

	@media (width >= theme(--breakpoint-sm)) {
		font-size: 2.25rem;
		line-height: 2.75rem;
	}

	@media (width >= theme(--breakpoint-lg)) {
		font-size: 2.5rem;
		line-height: 3rem;
	}
}

@utility heading-4 {
	@apply font-heading text-[1.75rem] leading-[2.25rem] font-bold;

	@media (width >= theme(--breakpoint-sm)) {
		font-size: 1.875rem;
		line-height: 2.375rem;
	}

	@media (width >= theme(--breakpoint-lg)) {
		font-size: 2rem;
		line-height: 2.5rem;
	}
}

@utility heading-5 {
	@apply font-heading text-[1.5rem] leading-[2rem] font-bold;

	@media (width >= theme(--breakpoint-sm)) {
		font-size: 1.5rem;
		line-height: 2rem;
	}

	@media (width >= theme(--breakpoint-lg)) {
		font-size: 1.5rem;
		line-height: 2rem;
	}
}

@utility heading-6 {
	@apply font-heading text-[1.25rem] leading-[1.75rem] font-bold;

	@media (width >= theme(--breakpoint-sm)) {
		font-size: 1.25rem;
		line-height: 1.75rem;
	}

	@media (width >= theme(--breakpoint-lg)) {
		font-size: 1.25rem;
		line-height: 1.75rem;
	}
}

@utility body-lg {
	/* Body text styles */
	@apply font-body text-[1.125rem] leading-[1.75rem];
}

@utility body-base {
	@apply font-body text-[1rem] leading-[1.5rem];
}

@utility body-15 {
	@apply font-body text-[0.9375rem] leading-[1.375];
}

@utility body-sm {
	@apply font-body text-[0.875rem] leading-[1.25rem];
}

@utility body-13 {
	@apply font-body text-[0.8125rem] leading-[1.125rem];
}

@utility body-xs {
	@apply font-body text-[0.75rem] leading-[1rem];
}

@utility no-scrollbar {
	-ms-overflow-style: none;
	/* IE and Edge */
	scrollbar-width: none;
	/* Firefox */

	&::-webkit-scrollbar {
		display: none;
		/* Chrome, Safari and Opera */
	}
}

@utility hide-stepper {
	/* Hide the number stepper for input[type="number"] */
	&input[type="number"]::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
		-moz-appearance: textfield !important;
	}
	& input[type="number"]::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
		-moz-appearance: textfield !important;
	}
	& input[type="number"] {
		-webkit-appearance: none;
		margin: 0;
		-moz-appearance: textfield !important;
	}
}

@layer base {
	button:not(:disabled),
	[role="button"]:not(:disabled) {
		cursor: pointer;
	}
}

@layer base {
	* {
		@apply border-stroke;
		scrollbar-width: thin;
		scrollbar-color: hsl(var(--border)) transparent;
		scroll-behavior: smooth;
	}

	html {
		font-family: "Inter", system-ui, sans-serif;
	}

	body {
		@apply bg-bg1 text-fg1;
	}

	code[data-line-numbers] {
		counter-reset: line;
	}

	code[data-line-numbers] > [data-line]::before {
		counter-increment: line;
		content: counter(line);

		/* Other styling */
		display: inline-block;
		width: 0.75rem;
		margin-right: 2rem;
		text-align: right;
		color: gray;
	}

	code[data-line-numbers-max-digits="2"] > [data-line]::before {
		width: 1.25rem;
	}

	code[data-line-numbers-max-digits="3"] > [data-line]::before {
		width: 1.75rem;
	}

	code[data-line-numbers-max-digits="4"] > [data-line]::before {
		width: 2.25rem;
	}
}

@layer utilities {
	/* Custom scrollbar styling. */
	::-webkit-scrollbar {
		width: 6px;
	}

	::-webkit-scrollbar-track {
		background: transparent;
	}

	::-webkit-scrollbar-thumb {
		background: hsl(var(--border));
		border-radius: 5px;
	}

	::-webkit-scrollbar-thumb:hover {
		background: hsl(var(--border-secondary));
	}

	/* Chrome & other browser puts a background color on autofill inputs,
	 this removes it */
	input:-webkit-autofill,
	input:-webkit-autofill:hover,
	input:-webkit-autofill:focus,
	input:-webkit-autofill:active {
		-webkit-background-clip: text;
		-webkit-text-fill-color: inherit;
		transition: background-color 5000s ease-in-out 0s;
		box-shadow: inset 0 0 20px 20px transparent;
	}

	.dark img {
		filter: none !important;
	}

	.dark .invert-in-dark {
		filter: invert(1) !important;
	}

	.svg-color {
		fill: var(--color-fg1);
	}
}
`
